import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { scheduleBody } from '@/settings/run-schedule/cadence'
import type { Schedule } from '@/settings/run-schedule/cadence'
import { RunScheduleForm } from '@/settings/run-schedule/form'
import { useActiveTenant } from '@/tenant/context'
import { ErrorState, Loading } from '@/ui/states'
import { NoSiteYet, SettingsStepShell } from './SettingsStepShell'
import { useWizard } from './context'
import { useSettleStep } from './settingsStep'

/** Step 9 — the run schedule for the wizard's site, through `/settings/run-schedule`'s form. */
export function RunScheduleStep(): JSX.Element {
  const state = useWizard()
  const { tenant } = useActiveTenant()
  const site = state.site ?? null
  const siteId = site?.id ?? null
  const schedule = useQuery(
    (signal) =>
      siteId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/run-schedules/{site_id}', {
              params: { path: { site_id: siteId } },
              signal,
            }),
          ),
    [siteId],
  )
  const save = useMutation((body: Schedule) =>
    call(() =>
      api.PUT('/api/v1/seo/run-schedules/{site_id}', {
        params: { path: { site_id: body.site_id } },
        body: scheduleBody(body),
      }),
    ),
  )
  const { settle, failure } = useSettleStep('run_schedule')

  return (
    <SettingsStepShell step="run_schedule" failure={failure}>
      {site === null ? <NoSiteYet /> : null}
      {site !== null && schedule.state.status === 'loading' ? (
        <Loading label="Loading" lines={2} />
      ) : null}
      {schedule.state.status === 'error' ? (
        <ErrorState failure={schedule.state.failure} onRetry={schedule.refetch} />
      ) : null}
      {site !== null && schedule.state.status === 'ready' && schedule.state.data !== null ? (
        <RunScheduleForm
          schedule={schedule.state.data}
          domain={site.domain}
          automationAvailable={tenant.automation_available}
          submitLabel="Continue"
          onSubmit={async (next) => {
            const result = await save.run(next)
            if (result.ok) await settle()
            return result
          }}
        />
      ) : null}
    </SettingsStepShell>
  )
}
